import { useEffect, useRef, useState } from 'react';
import type { AppSession, CoachTurn, PatientView } from '../../lib/types';
import { ApiError, api } from '../../lib/api';
import { firstName } from '../../lib/session';
import { AppMark, Icon } from '../../components/Mark';

interface Bubble {
  role: 'patient' | 'coach';
  text: string;
  escalate?: 'none' | 'clinic' | 'urgent';
}

/** What this patient might actually want to ask, from the track their treatment puts them on. */
export function suggestionsFor(view: PatientView): string[] {
  const track = view.track.id;
  if (track === 'chemotherapy') return ['What should I do if I get a temperature?', 'What can I eat when nothing tastes right?', 'Where am I in this cycle?'];
  if (track === 'immunotherapy') return ['My bowels have been loose — does that matter?', 'What should I watch for with this treatment?', 'How tired is too tired?'];
  if (track === 'oral-therapy') return ['What do I do about a missed dose?', 'My palms are sore — is that the tablets?', 'How do I remember them both every day?'];
  if (track === 'radiation') return ['How do I look after the skin?', 'Swallowing hurts — what helps?', 'When does the tiredness ease off?'];
  return ['How do I build my strength back up?', 'What symptoms should I still report?', 'How much should I be doing?'];
}

/** The coach, as a chat panel in the page. The first reply of the day counts as a check-in. */
export function CoachTab({ session, view, reload, say }: { session: AppSession; view: PatientView; reload: () => Promise<void>; say: (s: string) => void }) {
  const [bubbles, setBubbles] = useState<Bubble[]>(() => [
    { role: 'coach', text: `Hi ${firstName(view.profile.name)} — I am your OncoCoach. ${view.insight ?? 'How are you feeling today?'} Ask me about your treatment, your symptoms or your medications. If something is urgent, ring your team rather than waiting for me.` },
  ]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const bottom = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }, [bubbles.length, busy]);

  const ask = async (question: string) => {
    const q = question.trim();
    if (!q || busy) return;
    setText('');
    const history: CoachTurn[] = bubbles.slice(1).map((b) => ({ role: b.role, text: b.text }));
    setBubbles((bs) => [...bs, { role: 'patient', text: q }]);
    setBusy(true);
    try {
      const r = await api.coachAsk(q, history, session.token);
      setBubbles((bs) => [...bs, { role: 'coach', text: r.reply, escalate: r.escalate }]);
      if (r.checkedIn) await reload();
    } catch (e) {
      setBubbles((bs) => [...bs, { role: 'coach', text: e instanceof ApiError ? `I could not answer just now: ${e.message}` : 'I could not reach the coach just now. If this is urgent, ring your care team.' }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="shell-narrow" style={{ margin: '0 auto' }}>
      <div className="page-head">
        <div>
          <h1>Your coach</h1>
          <span className="hint">
            Available any time. {view.doctor.name} and the team at {view.practiceName} see anything urgent.
          </span>
        </div>
      </div>

      <section className="card chat-card">
        <header className="chat-head">
          <span className="bubble bubble-white bubble-sm">
            <AppMark size={18} />
          </span>
          <span className="row-main">
            <h2>OncoCoach AI</h2>
            <span className="hint light">
              <i className="live" /> Always here for you
            </span>
          </span>
        </header>

        <div className="chat">
          {bubbles.map((b, i) => (
            <div key={i} className={`bubble-msg ${b.role}`}>
              {b.role === 'coach' ? (
                <span className="bubble bubble-red bubble-xs" aria-hidden="true">
                  <AppMark size={12} />
                </span>
              ) : null}
              <div className="msg">
                {b.text.split('\n\n').map((para, j) => (
                  <p key={j}>{para}</p>
                ))}
                {b.escalate && b.escalate !== 'none' ? (
                  <span className={`badge ${b.escalate === 'urgent' ? 'badge-red' : 'badge-gold'}`}>
                    <Icon name="alert" size={12} /> {b.escalate === 'urgent' ? 'Urgent — your care team has been flagged' : 'Flagged to your care team'}
                  </span>
                ) : null}
              </div>
            </div>
          ))}
          {busy ? (
            <div className="bubble-msg coach">
              <span className="bubble bubble-red bubble-xs" aria-hidden="true">
                <AppMark size={12} />
              </span>
              <div className="msg typing">•••</div>
            </div>
          ) : null}
          <div ref={bottom} />
        </div>

        {bubbles.length === 1 ? (
          <div className="suggestions">
            {suggestionsFor(view).map((s) => (
              <button key={s} type="button" className="suggestion" onClick={() => void ask(s)}>
                {s}
              </button>
            ))}
          </div>
        ) : null}

        <form
          className="composer"
          onSubmit={(e) => {
            e.preventDefault();
            void ask(text);
          }}
        >
          <input type="text" value={text} placeholder="Ask your coach anything…" onChange={(e) => setText(e.target.value)} maxLength={2000} aria-label="Your question" />
          <button type="submit" className="send" disabled={!text.trim() || busy} aria-label="Send">
            ➤
          </button>
        </form>
      </section>

      <p className="hint center" style={{ marginTop: '0.9rem' }}>
        OncoCoach is a coach, not a doctor. It never changes your medications, and anything urgent goes to your care team.
      </p>
    </div>
  );
}
